import React, { useState } from "react";
import { Avatar } from "components/avatar";
import { Link } from "react-router-dom";
import { theme } from "styles/theme";
import axios from "axios";
import { useQuery } from "react-query";
import CustomFields from "./customFields";
import DataLabel from "components/dataLabel";
import { NoStyleA } from "components/button";
import { useTranslation } from 'react-i18next';

const Overview = (props) => {
  const { t } = useTranslation();
  const tBase = "views.property.overview";
  const tr = (key) => t(`${tBase}.${key}`);
  
  const propertyId = props.propertyId;
  const [propertyDetails] = useState({});

  const query = useQuery(
    ["property", propertyId],
    async () =>
      await axios
        .get(
          `
  ${process.env.REACT_APP_SERVER_URL}/api/v1/company/property/${propertyId}
  `
        )
        .then((res) => res.data)
  );

  const defaultTaxId = query.data?.default_tax;
  const defaultTaxGroupId = query.data?.default_tax_group;

  const getTaxQuery = useQuery(
    ["getTax", defaultTaxId],
    async () => await axios
      .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/tax/byId/${defaultTaxId}`)
      .then((res) => res.data),
    { enabled: query.isSuccess && !!defaultTaxId }
  );

  const getTaxOptionsQuery = useQuery(
    ["getTaxOptions", query.data?.id, defaultTaxGroupId],
    async () =>
      await axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/tax/both`)
        .then((res) => res.data),
    { enabled: query.isSuccess && !!defaultTaxGroupId }
  );

  const getTaxTypeLabel = (taxType) => {
    if (taxType === "fee") return tr("Fee");
    if (taxType === "discount") return tr("Discount");
    return tr("Tax");
  };

  const getTaxType = (taxType) => {
    if (taxType === "fixed") return tr("Fixed");
    if (taxType === "percent") return tr("Percent");
    return null;
  };

  const formatTaxAmount = (tax) => {
    if (!tax || tax.list_rate === null || tax.list_rate === undefined) return "N/A";
    if (tax.list_type === "fixed") {
      return `$${(Number(tax.list_rate) / 100).toFixed(2)}`;
    }
    return `${tax.list_rate}%`;
  };

  const defaultTaxGroup = React.useMemo(() => {
    if (!defaultTaxGroupId || !getTaxOptionsQuery.data?.taxGroups) return null;
    return (
      getTaxOptionsQuery.data.taxGroups.find(
        (group) => Number(group.id) === Number(defaultTaxGroupId)
      ) || null
    );
  }, [getTaxOptionsQuery.data, defaultTaxGroupId]);

  const singleTaxTypeLabel = getTaxTypeLabel(getTaxQuery.data?.tax_type);
  const defaultTaxTitle = defaultTaxGroupId
    ? tr("Combined Tax Rate")
    : query.data?.default_tax
    ? singleTaxTypeLabel
    : tr("No tax assigned");
  const defaultTaxLabel = defaultTaxGroupId
    ? defaultTaxGroup?.label || ""
    : getTaxQuery.data?.label || "";
  const singleTaxAmount = !defaultTaxGroupId && query.data?.default_tax
    ? formatTaxAmount(getTaxQuery.data)
    : "";
  const singleTaxType = !defaultTaxGroupId && query.data?.default_tax
    ? getTaxQuery.data?.list_type || "N/A"
    : "";

  return (
    <div>
      <div
        style={{
          marginTop: 10,
          paddingBottom: 20,
          borderBottom: `1px solid ${theme.border.light}`,
        }}
      >
        <h3>{tr("Associated Accounts")}</h3>
      </div>
      <div className="flex space-x-8 py-4">
        <DataLabel title={tr("Owner")}>
          {query.data?.owner ? (
            <NoStyleA to={`/customers/${query.data.owner.id}`}>
              <Avatar showLabel={true}>{query.data?.owner?.name}</Avatar>
            </NoStyleA>
          ) : (
            <span>{tr("No owner assigned")}</span>
          )}
        </DataLabel>
        <DataLabel title="Billed To">
          {query.data?.billed_to ? (
            <NoStyleA to={`/customers/${query.data.billed_to.id}`}>
              <Avatar showLabel={true}>{query.data?.billed_to?.name}</Avatar>
            </NoStyleA>
          ) : (
            <span>{tr("No billing account assigned")}</span>
          )}
        </DataLabel>
      </div>

      {/* Default Tax */}
      <div
        style={{
          marginTop: 10,
          paddingBottom: 20,
          borderBottom: `1px solid ${theme.border.light}`,
        }}
      >
        <h3>{tr("Default Tax")}</h3>
      </div>

      {/* Singular Tax */}
      <div className="flex space-x-8 py-4">
        <div className="flex items-center">
          <div className="min-w-[168px]">
            <div className="font-medium text-slate-500 text-sm m-0 p-0">{defaultTaxTitle}</div>
            <div>{defaultTaxLabel}</div>
          </div>
          <div>
            <div className="font-medium text-slate-500 text-sm m-0 p-0">{getTaxType(singleTaxType)}</div>
            <div>{singleTaxAmount}</div>
          </div>
        </div>
      </div>

      {/* Combined Tax */}
      {query.data?.default_tax_group && defaultTaxGroup?.taxes?.length > 0 ? (
        <div className="space-y-2 pb-4">
          {defaultTaxGroup.taxes.map((tax) => (
            <div
              key={tax.id}
              className="flex items-center justify-between text-sm border-b pb-2"
            >
              <div>{tax.label}</div>
              <div className="text-slate-600">
                {getTaxTypeLabel(tax.tax_type)} - {tax.list_type || "N/A"} - {formatTaxAmount(tax)}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {propertyDetails.customers &&
        propertyDetails.customers?.map((customer) => {
          return (
            <Link
              key={customer.id}
              style={{
                textDecoration: "none",
                cursor: "pointer",
                marginTop: 20,
                display: "flex",
                flex: 1,
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: `1px solid ${theme.border.light}`,
                paddingBottom: 20,
              }}
              to={`/customers/${customer.id}`}
            >
              <Avatar
                showLabel={true}
              >{`${customer.first_name} ${customer.last_name}`}</Avatar>
            </Link>
          );
        })}

      <CustomFields id={propertyId} />
    </div>
  );
};

export default Overview;
